import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseAPI } from '../../config';
import { toast } from 'react-toastify';
import ModalBasic from '../ModalBasic';
import {
  DEBT_ACTIVITY_TYPES,
  DEBT_ACTIVITY_LABELS,
} from '@claimup/shared/constants/expedient';

const DebtActivityModal = ({
  isOpen,
  onClose,
  onSave,
  expedientId,
  editingItem = null,
}) => {
  const [formData, setFormData] = useState({
    type: DEBT_ACTIVITY_TYPES.DEBT,
    title: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        type: editingItem.type || DEBT_ACTIVITY_TYPES.DEBT,
        title: editingItem.title || '',
        description: editingItem.description || '',
        amount: editingItem.amount || '',
        date: editingItem.date
          ? new Date(editingItem.date).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      });
    } else {
      setFormData({
        type: DEBT_ACTIVITY_TYPES.DEBT,
        title: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
      });
    }
  }, [editingItem, isOpen]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.type || !formData.title || !formData.amount) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount < 0) {
      toast.error('El importe debe ser un número positivo');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        type: formData.type,
        title: formData.title,
        description: formData.description,
        amount: amount,
        date: formData.date,
      };

      if (editingItem) {
        await axios.put(
          `${baseAPI}/expedients/${expedientId}/debt-activity/${editingItem._id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        toast.success('Movimiento actualizado');
      } else {
        await axios.post(
          `${baseAPI}/expedients/${expedientId}/debt-activity`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        toast.success('Movimiento añadido');
      }

      onSave();
    } catch (error) {
      console.error('Error saving debt activity:', error);
      toast.error(
        error.response?.data?.error ||
          'Error al guardar el movimiento'
      );
    } finally {
      setSaving(false);
    }
  };

  const activityTypes = Object.values(DEBT_ACTIVITY_TYPES);

  return (
    <ModalBasic
      id="debt-activity-modal"
      modalOpen={isOpen}
      setModalOpen={onClose}
      title={editingItem ? 'Editar Movimiento' : 'Añadir Movimiento'}
    >
      <form onSubmit={handleSubmit}>
        <div className="px-5 py-4">
          <div className="space-y-4">
            {/* Row 1: Tipo + Concepto */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="type"
                >
                  Tipo <span className="text-red-500">*</span>
                </label>
                <select
                  id="type"
                  name="type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  {activityTypes.map(type => (
                    <option key={type} value={type}>
                      {DEBT_ACTIVITY_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="title"
                >
                  Concepto <span className="text-red-500">*</span>
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Factura #12345"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Row 2: Importe + Fecha */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="amount"
                >
                  Importe <span className="text-red-500">*</span>
                </label>
                <input
                  id="amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="date"
                >
                  Fecha
                </label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Row 3: Descripción */}
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="description"
              >
                Descripción
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Información adicional (opcional)"
                value={formData.description}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? 'Guardando...' : editingItem ? 'Guardar cambios' : 'Añadir'}
            </button>
          </div>
        </div>
      </form>
    </ModalBasic>
  );
};

export default DebtActivityModal;
